/* -----------------Globals--------------- */
import React, { useCallback, useEffect, useMemo } from "react";

import { useUser } from "@clerk/clerk-expo";

type AuthContextValue = {
	viewerId?: string;
	loading?: boolean;
	refreshAuth?: () => void;
};

// constants
const initialContextValue: AuthContextValue = {
	viewerId: undefined,
	refreshAuth: () => {
		throw new Error("refreshAuth is not implemented");
	},
};

// context
const AuthContext = React.createContext<AuthContextValue>(initialContextValue);

/* -----------------Component--------------- */
const AuthProvider = ({
	children,
	identifyUser,
	app,
}: {
	children: React.ReactNode;
	identifyUser: ({
		userId,
		email,
		username,
		app,
	}: { userId: string; email: string; username: string; app: string }) => void;
	app: string;
}) => {
	const { user, isLoaded, isSignedIn } = useUser();

	const userId = user?.id;
	const email = user?.emailAddresses[0].emailAddress;
	const username = user?.username;

	const refreshAuth = useCallback(() => {
		if (isLoaded && isSignedIn && userId && email) {
			identifyUser({ userId, email, username: username || "", app });
		}
	}, [userId, email, username, app, identifyUser, isSignedIn, isLoaded]);

	useEffect(() => {
		refreshAuth();
	}, [refreshAuth]);

	const contextValue = useMemo(() => {
		return {
			isAuthenticated: !!user,
			user,
			viewerId: user ? (user.id as string | undefined) : "",
			loading: !isLoaded,
			refreshAuth,
		};
	}, [user, isLoaded, refreshAuth]);

	return (
		<AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
	);
};

const useAuthContext = () => {
	const context = React.useContext(AuthContext);
	if (context === undefined) {
		throw new Error("useAuthContext must be used within a AuthProvider");
	}
	return context;
};
export { AuthProvider, useAuthContext };
